import React from "react";
import { VirtualItem } from "@tanstack/react-virtual";
import { CalendarDefinition, Task, ViewMode } from "../../types/public-types";
import { addToDate } from "../../helpers/date-helper";
import { isOffDay } from "../../helpers/calendar-helper";
import styles from "./grid.module.css";

export type GridBodyProps = {
  tasks: Task[];
  scheduleType: string;
  dates: Date[];
  svgWidth: number;
  rowHeight: number;
  columnWidth: number;
  todayColor: string;
  weekendColor: string;
  rtl: boolean;
  virtualItems?: VirtualItem[];
  visibleStartY: number;
  visibleEndY: number;
  projectCalendar?: CalendarDefinition;
  viewMode?: ViewMode;
};

export const GridBody: React.FC<GridBodyProps> = ({
  tasks,
  scheduleType,
  dates,
  rowHeight,
  svgWidth,
  columnWidth,
  todayColor,
  weekendColor,
  rtl,
  virtualItems = [],
  visibleStartY,
  visibleEndY,
  projectCalendar,
  viewMode,
}) => {
  const visibleHeight = visibleEndY - visibleStartY;
  const now = new Date();

  // === Virtual row calculation ===
  const items =
    virtualItems.length > 0
      ? virtualItems
      : tasks.map((_, i) => ({
          index: i,
          start: i * rowHeight,
          end: (i + 1) * rowHeight,
          size: rowHeight,
          key: i,
        }));

  const isDayOff = (date: Date): boolean => {
    if (projectCalendar) return isOffDay(date, projectCalendar);
    const d = date.getDay();
    return d === 0 || d === 6;
  };

  const isTodayColumn = (i: number) => {
    const date = dates[i];
    const next = dates[i + 1];
    if (next && date <= now && next > now) return true;
    if (!next && date <= now) {
      // Estimate this (last) column's end by extrapolating the previous step.
      // A single-column range has no previous date, so fall back to one day
      // instead of dereferencing dates[-1].
      const prev = dates[i - 1];
      const step = prev ? date.getTime() - prev.getTime() : 86400000;
      const end = addToDate(date, step, "millisecond");
      return end > now;
    }
    return false;
  };

  // === Today, Off-Day, and Weekend Rects ===
  const todayRects: React.ReactNode[] = [];
  const offDayRects: React.ReactNode[] = [];
  const weekStartDay = projectCalendar?.week_start ?? 1;

  for (let i = 0, x = 0; i < dates.length; i++, x += columnWidth) {
    if (isTodayColumn(i)) {
      todayRects.push(
        <rect
          key={`today-${i}`}
          x={rtl ? x + columnWidth : x}
          y={visibleStartY}
          width={columnWidth}
          height={visibleHeight}
          fill={todayColor}
        />
      );
    }
    // Proportional off-day shading: Day=full column, Week=slivers; Month/Quarter excluded
    if (viewMode !== ViewMode.Month && viewMode !== ViewMode.Quarter) {
      const pEnd = dates[i + 1] ?? addToDate(dates[i], 1, "day");
      const periodMs = pEnd.getTime() - dates[i].getTime();
      if (periodMs > 0) {
        const cursor = new Date(dates[i]);
        cursor.setHours(0, 0, 0, 0);
        while (cursor.getTime() < pEnd.getTime()) {
          if (isDayOff(cursor)) {
            const ds = cursor.getTime() - dates[i].getTime();
            const de = ds + 86400000;
            const startFrac = Math.max(0, ds) / periodMs;
            const endFrac = Math.min(periodMs, de) / periodMs;
            const rw = (endFrac - startFrac) * columnWidth;
            if (rw > 0.5) {
              offDayRects.push(
                <rect
                  key={`offday-${i}-${cursor.getTime()}`}
                  x={x + startFrac * columnWidth}
                  y={visibleStartY}
                  width={rw}
                  height={visibleHeight}
                  fill={weekendColor}
                />
              );
            }
          }
          cursor.setDate(cursor.getDate() + 1);
        }
      }
    }
  }

  // === Vertical Tick Lines — only Day & Week views get the darker week-start
  // line; Day also dashes the intra-week days, every other view uses the light
  // default tick. ===
  const isDayView = viewMode === ViewMode.Day;
  const isWeekView = viewMode === ViewMode.Week;
  const tickLines = dates.map((date, i) => {
    let tickClass = styles.gridTick;
    if (isDayView) {
      tickClass =
        date.getDay() === weekStartDay
          ? styles.gridTickWeekStart
          : styles.gridTickDashed;
    } else if (isWeekView) {
      tickClass = styles.gridTickWeekStart;
    }
    return (
      <line
        key={`tick-${i}`}
        x1={i * columnWidth}
        y1={visibleStartY}
        x2={i * columnWidth}
        y2={visibleEndY}
        className={tickClass}
      />
    );
  });

  // === Horizontal Rows & Lines ===
  const rowBackgrounds: React.ReactNode[] = [];
  const rowLines: React.ReactNode[] = [];
  // Per-row overlay layer: erases chart-wide off-day shading inside task rows
  // (so the project calendar never affects task rendering), then re-applies
  // each task's own off-day shading from its linked calendar (task.calendar).
  const rowOverlays: React.ReactNode[] = [];

  // Top border
  rowLines.push(
    <line
      key="top-line"
      x1={0}
      y1={visibleStartY}
      x2={svgWidth}
      y2={visibleStartY}
      className={styles.gridRowLine}
    />
  );

  const showPerRowOffDays =
    viewMode !== ViewMode.Month && viewMode !== ViewMode.Quarter;

  for (const vi of items) {
    const task = tasks[vi.index];
    if (!task) break;

    const y = vi.start;
    const isMilestone = task.type === "milestone";

    const rowClass = isMilestone
      ? styles.darkerGridRow
      : scheduleType === "lookAhead"
      ? styles.gridRowLookAhead
      : styles.gridRow;

    rowBackgrounds.push(
      <rect
        key={`bg-${vi.key}`}
        x={0}
        y={y}
        width={svgWidth}
        height={rowHeight}
        className={rowClass}
      />
    );

    rowLines.push(
      <line
        key={`line-${vi.key}`}
        x1={0}
        y1={y + rowHeight}
        x2={svgWidth}
        y2={y + rowHeight}
        className={styles.gridRowLine}
      />
    );

    if (showPerRowOffDays) {
      // 1) Erase chart-wide project off-day shading inside this row.
      //    nth-child won't match in a sibling <g>, so compute the same fill
      //    explicitly (matches grid.module.css: gridRow alternates #fff/#f5f5f5).
      const rowEven = vi.index % 2 === 1;
      const rowFill = isMilestone
        ? "#e6e4e4"
        : scheduleType === "lookAhead"
        ? "#fff"
        : rowEven
        ? "#f5f5f5"
        : "#fff";
      rowOverlays.push(
        <rect
          key={`row-clean-${vi.key}`}
          x={0}
          y={y}
          width={svgWidth}
          height={rowHeight}
          fill={rowFill}
        />
      );

      // 2) Render this task's own off-days/holidays (if it has a calendar).
      const cal = task.calendar;
      if (cal) {
        for (let i = 0, x = 0; i < dates.length; i++, x += columnWidth) {
          const pEnd = dates[i + 1] ?? addToDate(dates[i], 1, "day");
          const periodMs = pEnd.getTime() - dates[i].getTime();
          if (periodMs <= 0) continue;
          const cursor = new Date(dates[i]);
          cursor.setHours(0, 0, 0, 0);
          while (cursor.getTime() < pEnd.getTime()) {
            if (isOffDay(cursor, cal)) {
              const ds = cursor.getTime() - dates[i].getTime();
              const de = ds + 86400000;
              const startFrac = Math.max(0, ds) / periodMs;
              const endFrac = Math.min(periodMs, de) / periodMs;
              const rw = (endFrac - startFrac) * columnWidth;
              if (rw > 0.5) {
                rowOverlays.push(
                  <rect
                    key={`row-offday-${vi.key}-${cursor.getTime()}`}
                    x={rtl ? x + columnWidth - (startFrac * columnWidth + rw) : x + startFrac * columnWidth}
                    y={y}
                    width={rw}
                    height={rowHeight}
                    fill={weekendColor}
                  />
                );
              }
            }
            cursor.setDate(cursor.getDate() + 1);
          }
        }
      }
    }
  }

  return (
    <g className="gridBody">
      <g className="rows">{rowBackgrounds}</g>
      <g className="rowLines">{rowLines}</g>
      <g className="offdays">{offDayRects}</g>
      <g className="rowOverlays">{rowOverlays}</g>
      <g className="ticks">{tickLines}</g>
      <g className="today">{todayRects}</g>
    </g>
  );
};
